import React, { useCallback, useMemo, useState } from 'react';

import {
  getIsDefaultOrgInsightSystemChartPresetId,
  getIsWorkflowSystemChartPresetId,
  getWorkflowSystemChartPreset,
  getWorkflowViewStateFromSystemChartPreset,
  parseWorkflowViewState,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS,
} from '@aline/common';
import type { WorkflowSystemChartPreset } from '@aline/common';
import { link, params } from '@aline/routes';
import { Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { ChartColumn, ChevronDown, Plus, RotateCcw } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { useCurrentUserQuery } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { useCreateDraftViewAndOpenBuilder } from './create-draft-view-and-open-builder';
import { CreateInsightChartDialog } from './CreateInsightChartDialog';
import {
  getChartTypeFromInsightPin,
  getSeriesInputFromInsightPin,
  getSeriesInputFromSystemPreset,
} from './insight-chart-series';
import { InsightChartCard, InsightChartCardSkeleton } from './InsightChartCard';
import type { InsightChartCardProps } from './InsightChartCard';

const useStyles = makeStyles()(theme => ({
  emptyCopy: {
    maxWidth: 380,
  },
  emptyIcon: {
    alignItems: 'center',
    backgroundColor: theme.appColors.selectedBackgroundNav,
    borderRadius: '50%',
    color: theme.palette.primary.main,
    display: 'flex',
    height: 44,
    justifyContent: 'center',
    marginBottom: theme.spacing(0.5),
    width: 44,
  },
  emptyState: {
    alignItems: 'center',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    paddingBlock: theme.spacing(5),
    paddingInline: theme.spacing(3),
    textAlign: 'center',
  },
  emptyStateAction: {
    marginTop: theme.spacing(1.5),
  },
  grid: {
    containerType: 'inline-size',
    display: 'grid',
    gap: theme.spacing(2),
    gridAutoFlow: 'dense',
    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
  },
  page: {
    display: 'flex',
    /**
     * The Workflows shell clips its overflow, so the Charts stack has to own a
     * scroll area of its own or everything past the fold is unreachable.
     */
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(3),
    minHeight: 0,
    overflowY: 'auto',
    paddingBlock: theme.spacing(1, 2),
  },
  restoreButton: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
      color: theme.palette.primary.main,
    },
    borderRadius: 16,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    paddingInline: theme.spacing(1.25),
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  restoreMenuCaption: {
    color: theme.palette.text.secondary,
    display: 'block',
    padding: theme.spacing(0.5, 2, 1),
  },
  section: {
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(1),
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  sectionCount: {
    backgroundColor: theme.appColors.darkBackground,
    borderRadius: 10,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    lineHeight: '20px',
    minWidth: 20,
    paddingInline: theme.spacing(0.75),
    textAlign: 'center',
  },
  /** Carries the group heading across the grid so sections read as separated. */
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 32,
  },
  sectionRule: {
    backgroundColor: theme.palette.divider,
    flex: 1,
    height: 1,
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
}));

/** One Org or Personal Insights card; the two selection sets are identical. */
type InsightsDashboardEntry =
  Apollo.GetWorkflowInsightsDashboardQuery['workflowInsightsDashboard']['org'][number];

interface InsightsPageProps {
  /** Filters Chart cards by title; empty shows everything. */
  search?: string;
}

export function InsightsPage({ search = '' }: InsightsPageProps) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useCurrentUserQuery();
  const matchesSearch = getTitleMatcher(search);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [presetPendingRemoval, setPresetPendingRemoval] =
    useState<WorkflowSystemChartPreset | null>(null);
  const [presetsPendingRestore, setPresetsPendingRestore] = useState<WorkflowSystemChartPreset[]>(
    []
  );
  const isOrgAdmin = currentUser?.role === Apollo.OrgRole.Admin;

  const {
    data: dashboardData,
    loading: dashboardLoading,
    refetch: refetchDashboard,
  } = Apollo.useGetWorkflowInsightsDashboardQuery({
    fetchPolicy: 'cache-and-network',
  });
  const dashboard = dashboardData?.workflowInsightsDashboard;
  /** Ids no longer defined in code are inert on the board, so they stay out of the menu. */
  const removedPresets = useMemo(
    () =>
      (dashboard?.hiddenSystemPresetIds ?? []).flatMap(presetId => {
        if (!getIsDefaultOrgInsightSystemChartPresetId(presetId)) return [];
        const preset = getSourceSystemPreset(presetId);
        return preset ? [preset] : [];
      }),
    [dashboard?.hiddenSystemPresetIds]
  );
  const { createDraftViewAndOpenBuilder } = useCreateDraftViewAndOpenBuilder();
  const [materializeSystemInsightChart, { loading: materializingSystemChart }] =
    Apollo.useMaterializeSystemInsightChartMutation();
  const [setSystemInsightChartHidden, { loading: hidingSystemChart }] =
    Apollo.useSetSystemInsightChartHiddenMutation();

  const handleEditSystemChart = useCallback(
    async (presetId: string) => {
      if (materializingSystemChart) return;
      try {
        const result = await materializeSystemInsightChart({ variables: { presetId } });
        const pin = result.data?.materializeSystemInsightChart.insightPin;
        if (!pin) {
          enqueueSnackbar(result.error?.message || 'Failed to edit system chart.', {
            variant: 'error',
          });
          return;
        }
        navigate(getInsightBuilderLink(pin));
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Failed to edit system chart.', {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, materializeSystemInsightChart, materializingSystemChart, navigate]
  );

  const handleDuplicateSystemChart = useCallback(
    async (preset: WorkflowSystemChartPreset) => {
      const { chartId, state } = getWorkflowViewStateFromSystemChartPreset(preset);
      await createDraftViewAndOpenBuilder({
        chartId,
        failureMessage: 'Failed to copy system chart.',
        name: `${preset.name} copy`,
        state,
      });
    },
    [createDraftViewAndOpenBuilder]
  );

  const getPresetRemoveAction = useCallback(
    (preset: WorkflowSystemChartPreset) => ({
      disabledReason: isOrgAdmin
        ? undefined
        : 'Only org admins can remove charts from Org Insights.',
      label: 'Remove from Org Insights',
      onRemove: () => setPresetPendingRemoval(preset),
    }),
    [isOrgAdmin]
  );

  const handleRemovePresetChart = useCallback(
    async (presetId: string) => {
      try {
        const result = await setSystemInsightChartHidden({
          variables: { hidden: true, presetId },
        });
        if (!result.data?.setSystemInsightChartHidden) {
          enqueueSnackbar(result.error?.message || 'Failed to remove chart.', {
            variant: 'error',
          });
          return;
        }
        setPresetPendingRemoval(null);
        await refetchDashboard();
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Failed to remove chart.', {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, refetchDashboard, setSystemInsightChartHidden]
  );

  const handleRestorePresetCharts = useCallback(
    async (presetIds: string[]) => {
      try {
        const results = await Promise.all(
          presetIds.map(presetId =>
            setSystemInsightChartHidden({ variables: { hidden: false, presetId } })
          )
        );
        const failedResult = results.find(result => !result.data?.setSystemInsightChartHidden);
        if (failedResult) {
          enqueueSnackbar(failedResult.error?.message || 'Failed to restore system charts.', {
            variant: 'error',
          });
          return;
        }
        await refetchDashboard();
        setRestoreDialogOpen(false);
        setPresetsPendingRestore([]);
        enqueueSnackbar(presetIds.length === 1 ? 'Chart restored.' : 'System charts restored.', {
          variant: 'success',
        });
      } catch (error) {
        enqueueSnackbar(
          error instanceof Error ? error.message : 'Failed to restore system charts.',
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, refetchDashboard, setSystemInsightChartHidden]
  );
  const handleDuplicatePinnedChart = useCallback(
    async (pin: { chartId: string; view: { id: string; name: string; state: unknown } }) => {
      await createDraftViewAndOpenBuilder({
        chartId: pin.chartId,
        failureMessage: 'Failed to copy chart.',
        name: `${pin.view.name} copy`,
        state: parseWorkflowViewState(pin.view.state),
      });
    },
    [createDraftViewAndOpenBuilder]
  );

  return (
    <div className={classes.page}>
      <InsightsSection
        emptyCopy={
          search
            ? 'Try a different search to find a published chart.'
            : 'Charts published to your organization show up here. Totals follow each viewer’s access, so they can differ from coworker to coworker.'
        }
        emptyTitle={search ? 'No matching org charts' : 'No org charts yet'}
        headerAction={
          isOrgAdmin &&
          Boolean(removedPresets.length) && (
            <RestoreRemovedChartsMenu
              disabled={hidingSystemChart}
              presets={removedPresets}
              onSelect={presets => {
                setPresetsPendingRestore(presets);
                setRestoreDialogOpen(true);
              }}
            />
          )
        }
        loading={dashboardLoading}
        skeleton={<InsightsGridSkeleton />}
        title="Org Insights"
      >
        {(dashboard?.org ?? []).map(entry => {
          if (entry.presetId && getIsWorkflowSystemChartPresetId(entry.presetId)) {
            const preset = getWorkflowSystemChartPreset(entry.presetId);
            if (!matchesSearch(preset.name)) return null;
            return (
              <InsightChartCard
                key={entry.id}
                actions={{
                  editLabel: isOrgAdmin ? 'Edit' : 'Duplicate and edit',
                  onEdit: () => {
                    if (!isOrgAdmin) {
                      void handleDuplicateSystemChart(preset);
                      return;
                    }
                    void handleEditSystemChart(preset.id);
                  },
                }}
                chartType={preset.chartType}
                description={preset.description}
                drilldown={{ presetId: preset.id }}
                publisher={{ type: 'system' }}
                removeAction={getPresetRemoveAction(preset)}
                series={entry.series}
                seriesInput={getSeriesInputFromSystemPreset(preset)}
                title={preset.name}
                wide={getIsWideSystemChart(preset.id)}
              />
            );
          }
          const { pin } = entry;
          const cardProps = getPinnedChartCardProps(entry, matchesSearch);
          if (!pin || !cardProps) return null;
          const sourcePreset = getSourceSystemPreset(pin.sourceSystemPresetId);
          return (
            <InsightChartCard
              key={entry.id}
              actions={{
                editLabel: isOrgAdmin ? 'Edit' : 'Duplicate and edit',
                onEdit: () => {
                  if (!isOrgAdmin) {
                    void handleDuplicatePinnedChart(pin);
                    return;
                  }
                  navigate(getInsightBuilderLink(pin));
                },
              }}
              removeAction={sourcePreset ? getPresetRemoveAction(sourcePreset) : undefined}
              {...cardProps}
            />
          );
        })}
      </InsightsSection>

      <InsightsSection
        emptyAction={
          <Button
            disableElevation
            size="small"
            startIcon={<Plus size={16} />}
            variant="contained"
            onClick={() => setCreateDialogOpen(true)}
          >
            Add chart
          </Button>
        }
        emptyCopy={
          search
            ? 'Try a different search to find one of your charts.'
            : 'Build a chart for yourself. Only you can see it until you publish it to your organization.'
        }
        emptyTitle={search ? 'No matching charts' : 'No personal charts yet'}
        loading={dashboardLoading}
        skeleton={<InsightChartCardSkeleton />}
        title="Personal Insights"
      >
        {(dashboard?.personal ?? []).map(entry => {
          const { pin } = entry;
          const cardProps = getPinnedChartCardProps(entry, matchesSearch);
          if (!pin || !cardProps) return null;

          return (
            <InsightChartCard
              key={entry.id}
              actions={{ onEdit: () => navigate(getInsightBuilderLink(pin)) }}
              {...cardProps}
            />
          );
        })}
      </InsightsSection>
      <CreateInsightChartDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      <ConfirmationDialog
        message={`Remove “${presetPendingRemoval?.name ?? ''}” from Org Insights? Any edits your organization made to it are deleted, and restoring brings back the original system chart.`}
        open={Boolean(presetPendingRemoval)}
        primaryDisabledReason={hidingSystemChart ? 'Removing chart…' : ''}
        primaryText="Remove chart"
        title="Remove chart?"
        onClickPrimary={() => {
          if (presetPendingRemoval) void handleRemovePresetChart(presetPendingRemoval.id);
        }}
        onClose={() => setPresetPendingRemoval(null)}
      />
      <ConfirmationDialog
        message={getRestorePresetMessage(presetsPendingRestore)}
        open={restoreDialogOpen}
        primaryDisabledReason={hidingSystemChart ? 'Restoring chart…' : ''}
        primaryText={getRestorePresetPrimaryText(presetsPendingRestore)}
        title={getRestorePresetTitle(presetsPendingRestore)}
        variant={ConfirmationDialogVariant.Info}
        onClickPrimary={() =>
          handleRestorePresetCharts(presetsPendingRestore.map(preset => preset.id))
        }
        onClose={() => setRestoreDialogOpen(false)}
      />
    </div>
  );
}

/**
 * Card props for a pinned Chart, shared by the Org and Personal grids — they differ
 * only in their edit action. `null` means the Chart is filtered out by the search or
 * has no Dimensions left to query.
 */
function getPinnedChartCardProps(
  entry: InsightsDashboardEntry,
  matchesSearch: (title: string) => boolean
): Pick<
  InsightChartCardProps,
  | 'chartType'
  | 'description'
  | 'drilldown'
  | 'publisher'
  | 'series'
  | 'seriesInput'
  | 'title'
  | 'wide'
> | null {
  const { pin } = entry;
  if (!pin) return null;
  const title = getInsightChartTitle(pin.view.state, pin.chartId, pin.view.name);
  if (!matchesSearch(title)) return null;
  const seriesInput = getSeriesInputFromInsightPin({
    chartId: pin.chartId,
    viewState: pin.view.state,
  });
  if (!seriesInput) return null;
  const sourcePreset = getSourceSystemPreset(pin.sourceSystemPresetId);

  return {
    chartType: getChartTypeFromInsightPin({ chartId: pin.chartId, viewState: pin.view.state }),
    description: sourcePreset?.description,
    drilldown: { viewState: pin.view.state },
    publisher: sourcePreset ? { type: 'system' } : { type: 'user', user: pin.owner },
    series: entry.series,
    seriesInput,
    title,
    wide: sourcePreset ? getIsWideSystemChart(sourcePreset.id) : false,
  };
}

function RestoreRemovedChartsMenu({
  disabled,
  onSelect,
  presets,
}: {
  onSelect: (presets: WorkflowSystemChartPreset[]) => void;
  presets: WorkflowSystemChartPreset[];
  disabled?: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const select = (nextPresets: WorkflowSystemChartPreset[]) => {
    setAnchorEl(null);
    onSelect(nextPresets);
  };

  return (
    <>
      <Button
        className={classes.restoreButton}
        disabled={disabled}
        endIcon={<ChevronDown size={14} />}
        size="small"
        startIcon={<RotateCcw size={14} />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        {presets.length} removed chart{presets.length === 1 ? '' : 's'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
        onClose={() => setAnchorEl(null)}
      >
        <Typography className={classes.restoreMenuCaption} variant="caption">
          Restore to Org Insights
        </Typography>
        {presets.map(preset => (
          <MenuItem key={preset.id} onClick={() => select([preset])}>
            {preset.name}
          </MenuItem>
        ))}
        {presets.length > 1 && <Divider />}
        {presets.length > 1 && <MenuItem onClick={() => select(presets)}>Restore all</MenuItem>}
      </Menu>
    </>
  );
}

function getRestorePresetMessage(presets: WorkflowSystemChartPreset[]): string {
  if (presets.length === 1) {
    return `Restore “${presets[0]?.name ?? ''}” to Org Insights? It comes back as the original system chart.`;
  }

  return `Restore ${presets.length} removed charts to Org Insights? They come back as the original system charts.`;
}

function getRestorePresetTitle(presets: WorkflowSystemChartPreset[]): string {
  if (presets.length === 1) return 'Restore chart?';
  return 'Restore charts?';
}

function getRestorePresetPrimaryText(presets: WorkflowSystemChartPreset[]): string {
  if (presets.length === 1) return 'Restore chart';
  return 'Restore charts';
}

/** The System preset an Org Pin was materialized from, if it came from one. */
function getSourceSystemPreset(
  sourceSystemPresetId: string | null | undefined
): WorkflowSystemChartPreset | null {
  if (!sourceSystemPresetId || !getIsWorkflowSystemChartPresetId(sourceSystemPresetId)) return null;
  return getWorkflowSystemChartPreset(sourceSystemPresetId);
}

function getInsightBuilderLink(pin: { chartId: string; view: { id: string } }): string {
  return `${link.WorkflowInsightBuilder({ viewId: pin.view.id })}?${params.insightChart}=${pin.chartId}`;
}

/** Time-series Charts that read best across the full grid width. */
function getIsWideSystemChart(presetId: string): boolean {
  return (
    presetId === WORKFLOW_SYSTEM_CHART_PRESET_IDS.createdCompletedWeekly ||
    presetId === WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalCompletionByTypeMonthly ||
    presetId === WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalTimeInStatusMonthly
  );
}

function getInsightChartTitle(state: unknown, chartId: string, fallback: string): string {
  return parseWorkflowViewState(state).chartsIntegration?.charts?.[chartId]?.title ?? fallback;
}

function getTitleMatcher(search: string): (title: string) => boolean {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return () => true;
  return title => title.toLocaleLowerCase().includes(query);
}

/** Mirrors the Org grid at rest: a wide Chart beside a standard one, then a full row. */
function InsightsGridSkeleton(): JSX.Element {
  return (
    <>
      <InsightChartCardSkeleton wide />
      <InsightChartCardSkeleton />
      <InsightChartCardSkeleton />
      <InsightChartCardSkeleton />
      <InsightChartCardSkeleton />
    </>
  );
}

function InsightsSection({
  children,
  emptyAction,
  emptyCopy,
  emptyTitle,
  headerAction,
  loading,
  skeleton,
  title,
}: {
  children: React.ReactNode;
  emptyCopy: string;
  emptyTitle: string;
  loading: boolean;
  skeleton: React.ReactNode;
  title: string;
  emptyAction?: React.ReactNode;
  headerAction?: React.ReactNode;
}): JSX.Element {
  const { classes } = useStyles();
  const childArray = React.Children.toArray(children).filter(Boolean);

  return (
    <section className={classes.section}>
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle} component="h2" variant="subtitle1">
          {title}
        </Typography>
        {!loading && (
          <Typography className={classes.sectionCount} component="span" variant="caption">
            {childArray.length}
          </Typography>
        )}
        <span className={classes.sectionRule} />
        {headerAction}
      </div>
      <InsightsSectionContent
        emptyAction={emptyAction}
        emptyCopy={emptyCopy}
        emptyTitle={emptyTitle}
        loading={loading}
        skeleton={skeleton}
      >
        {childArray}
      </InsightsSectionContent>
    </section>
  );
}

function InsightsSectionContent({
  children,
  emptyAction,
  emptyCopy,
  emptyTitle,
  loading,
  skeleton,
}: {
  children: React.ReactNode[];
  emptyCopy: string;
  emptyTitle: string;
  loading: boolean;
  skeleton: React.ReactNode;
  emptyAction?: React.ReactNode;
}): JSX.Element {
  const { classes } = useStyles();

  if (loading && children.length) {
    return <div className={classes.grid}>{children}</div>;
  }

  if (loading) {
    return <div className={classes.grid}>{skeleton}</div>;
  }

  if (!children.length) {
    return (
      <div className={classes.emptyState}>
        <span className={classes.emptyIcon}>
          <ChartColumn size={20} />
        </span>
        <Typography variant="subtitle2">{emptyTitle}</Typography>
        <Typography className={classes.emptyCopy} color="textSecondary" variant="body2">
          {emptyCopy}
        </Typography>
        {Boolean(emptyAction) && <div className={classes.emptyStateAction}>{emptyAction}</div>}
      </div>
    );
  }

  return <div className={classes.grid}>{children}</div>;
}
